import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../App';
import { useModalManager } from '../hooks/useModalManager';
import useBatchProcessor from '../hooks/useBatchProcessor';
import useBatchLoader from '../hooks/useBatchLoader';
import { useDragToScroll } from '../hooks/useDragToScroll';
import BatchProgressModal from '../components/BatchProgressModal';
import { isPro } from '../Helpers';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const BulkOrderEditor = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showSuccess, showError } = useToast();
    const { showPremiumModal } = useModalManager();
    const { batchProgress, processBatch, resetProgress, shouldUseBatchProcessing } = useBatchProcessor();
    const { loadingProgress, batchLoadItems, resetProgress: resetLoadingProgress, shouldUseBatchLoading } = useBatchLoader();
    
    const selectedOrderIds = location.state?.selectedOrders || [];
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({});
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [usingBatchLoading, setUsingBatchLoading] = useState(false);
    
    // Check if selection exceeds free limit
    const BULK_EDIT_LIMIT = 100;
    const isOverLimit = !isPro() && selectedOrderIds.length > BULK_EDIT_LIMIT;
    
    // Edit mode: 'common' or 'individual'
    const [editMode, setEditMode] = useState('common');
    
    // Common fields to apply to all orders
    const [commonFields, setCommonFields] = useState({
        status: '',
        payment_method: '',
        payment_method_title: '',
        transaction_id: '',
        customer_note: '',
        // Billing fields
        billing_first_name: '',
        billing_last_name: '',
        billing_email: '',
        billing_phone: '',
        billing_address_1: '',
        billing_address_2: '',
        billing_city: '',
        billing_state: '',
        billing_postcode: '',
        billing_country: '',
        // Shipping fields
        shipping_first_name: '',
        shipping_last_name: '',
        shipping_address_1: '',
        shipping_address_2: '',
        shipping_city: '',
        shipping_state: '',
        shipping_postcode: '',
        shipping_country: '',
    });
    
    // Toggle states for each field
    const [fieldToggles, setFieldToggles] = useState({
        status: true,
        payment_method: true,
        payment_method_title: false,
        transaction_id: false,
        customer_note: false,
        // Billing toggles
        billing_first_name: false,
        billing_last_name: false,
        billing_email: false,
        billing_phone: false,
        billing_address_1: false,
        billing_address_2: false,
        billing_city: false,
        billing_state: false,
        billing_postcode: false,
        billing_country: false,
        // Shipping toggles
        shipping_first_name: false,
        shipping_last_name: false,
        shipping_address_1: false,
        shipping_address_2: false,
        shipping_city: false,
        shipping_state: false,
        shipping_postcode: false,
        shipping_country: false,
    });
    
    // Individual order edits
    const [orderEdits, setOrderEdits] = useState({});

    // Drag-to-scroll functionality for the table
    const tableScrollRef = useDragToScroll({
        horizontal: true,
        vertical: true
    });

    useEffect(() => {
        if (selectedOrderIds.length === 0) {
            showError('No orders selected');
            navigate('/orders');
            return;
        }
        initializeData();
    }, []);

    const initializeData = async () => {
        try {
            // Load settings first and wait for them
            const loadedSettings = await loadSettings();
            // Then load orders with settings available
            await loadOrders(loadedSettings);
        } catch (error) {
            showError('Failed to initialize bulk editor');
        }
    };

    const loadSettings = async () => {
        try {
            const response = await api.getSettings();
            if (response.success) {
                setSettings(response.settings);
                console.log('📋 Settings loaded:', response.settings);
                return response.settings;
            }
            return {};
        } catch (error) {
            console.error('Failed to load settings:', error);
            return {};
        }
    };

    const loadOrders = async (loadedSettings = settings) => {
        try {
            setLoading(true);
            
            console.log(`🔍 Loading ${selectedOrderIds.length} orders...`);
            console.log('📋 Current settings:', loadedSettings);
            
            // Check if we should use batch loading
            const shouldUseBatch = shouldUseBatchLoading(selectedOrderIds.length, loadedSettings);
            
            console.log('🤔 Should use batch loading?', {
                itemCount: selectedOrderIds.length,
                settings: loadedSettings,
                shouldUseBatch: shouldUseBatch
            });
            
            if (shouldUseBatch) {
                console.log('🚀 Using batch loading - showing progress modal');
                setUsingBatchLoading(true);
                setLoading(false); // Don't show regular loading when using batch loading
                setShowLoadingModal(true);
            }
            
            // Load orders using batch loading if needed
            const loadedOrders = await batchLoadItems(selectedOrderIds, 'orders', 100, loadedSettings);
            
            setOrders(loadedOrders);
            
            // Initialize order edits
            const edits = {};
            loadedOrders.forEach(order => {
                edits[order.id] = {
                    status: order.status,
                    payment_method: order.payment_method,
                    payment_method_title: order.payment_method_title,
                    transaction_id: order.transaction_id || '',
                    customer_note: order.customer_note || '',
                    // Billing fields
                    billing_first_name: order.billing?.first_name || '',
                    billing_last_name: order.billing?.last_name || '',
                    billing_email: order.billing?.email || '',
                    billing_phone: order.billing?.phone || '',
                    billing_address_1: order.billing?.address_1 || '',
                    billing_address_2: order.billing?.address_2 || '',
                    billing_city: order.billing?.city || '',
                    billing_state: order.billing?.state || '',
                    billing_postcode: order.billing?.postcode || '',
                    billing_country: order.billing?.country || '',
                    // Shipping fields
                    shipping_first_name: order.shipping?.first_name || '',
                    shipping_last_name: order.shipping?.last_name || '',
                    shipping_address_1: order.shipping?.address_1 || '',
                    shipping_address_2: order.shipping?.address_2 || '',
                    shipping_city: order.shipping?.city || '',
                    shipping_state: order.shipping?.state || '',
                    shipping_postcode: order.shipping?.postcode || '',
                    shipping_country: order.shipping?.country || '',
                };
            });
            setOrderEdits(edits);
            
            console.log(`✅ Successfully loaded ${loadedOrders.length} orders`);
            
        } catch (error) {
            showError(error.message || 'Failed to load orders');
        } finally {
            setLoading(false);
            setShowLoadingModal(false);
            setUsingBatchLoading(false);
        }
    };

    const handleCommonFieldChange = (field, value) => {
        setCommonFields(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleOrderFieldChange = (orderId, field, value) => {
        setOrderEdits(prev => ({
            ...prev,
            [orderId]: {
                ...prev[orderId],
                [field]: value
            }
        }));
    };

    // Helper function to format order data for API
    const formatOrderDataForAPI = (orderData) => {
        const formatted = {
            status: orderData.status,
            payment_method: orderData.payment_method,
            payment_method_title: orderData.payment_method_title,
            transaction_id: orderData.transaction_id,
            customer_note: orderData.customer_note,
            billing: {
                first_name: orderData.billing_first_name || '',
                last_name: orderData.billing_last_name || '',
                email: orderData.billing_email || '',
                phone: orderData.billing_phone || '',
                address_1: orderData.billing_address_1 || '',
                address_2: orderData.billing_address_2 || '',
                city: orderData.billing_city || '',
                state: orderData.billing_state || '',
                postcode: orderData.billing_postcode || '',
                country: orderData.billing_country || '',
            },
            shipping: {
                first_name: orderData.shipping_first_name || '',
                last_name: orderData.shipping_last_name || '',
                address_1: orderData.shipping_address_1 || '',
                address_2: orderData.shipping_address_2 || '',
                city: orderData.shipping_city || '',
                state: orderData.shipping_state || '',
                postcode: orderData.shipping_postcode || '',
                country: orderData.shipping_country || '',
            }
        };
        
        return formatted;
    };

    const applyCommonFields = async () => {
        if (isOverLimit) {
            showPremiumModal();
            return;
        }
        
        try {
            setSaving(true);
            
            // Apply common fields to orderEdits first
            const updatedEdits = { ...orderEdits };
            let hasChanges = false;
            
            Object.keys(updatedEdits).forEach(orderId => {
                Object.keys(commonFields).forEach(field => {
                    // Only apply if field toggle is ON and has a value
                    if (fieldToggles[field] && commonFields[field] !== '' && commonFields[field] !== null && commonFields[field] !== undefined) {
                        updatedEdits[orderId][field] = commonFields[field];
                        hasChanges = true;
                    }
                });
            });
            
            if (!hasChanges) {
                showError('No fields to apply. Toggle fields ON and set values.');
                setSaving(false);
                return;
            }

            const orderIds = Object.keys(updatedEdits);
            const shouldUseBatch = shouldUseBatchProcessing(orderIds.length, settings);

            console.log('🔍 Apply Common Fields - Processing Decision:', {
                orderCount: orderIds.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);
                
                // Prepare batch data
                const batchData = Object.keys(updatedEdits).map(orderId => ({
                    id: orderId,
                    data: formatOrderDataForAPI(updatedEdits[orderId])
                }));

                await processBatch(
                    orderIds,
                    'bulkUpdate',
                    { updates: batchData },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'orders'
                );

                showSuccess(`Successfully applied common fields to ${orderIds.length} orders using batch processing`);
                setOrderEdits(updatedEdits);
            } else {
                // Use regular processing for smaller datasets
                const updatePromises = Object.keys(updatedEdits).map(orderId => {
                    const formattedData = formatOrderDataForAPI(updatedEdits[orderId]);
                    return api.updateOrder(orderId, formattedData);
                });
                
                const results = await Promise.all(updatePromises);
                const successCount = results.filter(r => r.success).length;
                
                if (successCount === results.length) {
                    showSuccess(`Successfully applied common fields to ${successCount} orders`);
                    setOrderEdits(updatedEdits);
                } else {
                    showError(`Updated ${successCount} of ${results.length} orders`);
                }
            }
            
        } catch (error) {
            showError('Failed to apply common fields');
        } finally {
            setSaving(false);
        }
    };
    
    const applyIndividualChanges = async () => {
        if (isOverLimit) {
            showPremiumModal();
            return;
        }
        
        try {
            setSaving(true);

            const orderIds = Object.keys(orderEdits);
            const shouldUseBatch = shouldUseBatchProcessing(orderIds.length, settings);

            console.log('🔍 Apply Individual Changes - Processing Decision:', {
                orderCount: orderIds.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);
                
                // Prepare batch data
                const batchData = Object.keys(orderEdits).map(orderId => ({
                    id: orderId,
                    data: formatOrderDataForAPI(orderEdits[orderId])
                }));

                await processBatch(
                    orderIds,
                    'bulkUpdate',
                    { updates: batchData },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'orders'
                );

                showSuccess(`Successfully updated ${orderIds.length} orders with individual changes using batch processing`);
            } else {
                // Use regular processing for smaller datasets
                const updatePromises = Object.keys(orderEdits).map(orderId => {
                    const formattedData = formatOrderDataForAPI(orderEdits[orderId]);
                    return api.updateOrder(orderId, formattedData);
                });
                
                const results = await Promise.all(updatePromises);
                const successCount = results.filter(r => r.success).length;
                
                if (successCount === results.length) {
                    showSuccess(`Successfully updated ${successCount} orders with individual changes`);
                } else {
                    showError(`Updated ${successCount} of ${results.length} orders`);
                }
            }
            
        } catch (error) {
            showError('Failed to save individual changes');
        } finally {
            setSaving(false);
        }
    };

    const toggleField = (field) => {
        setFieldToggles(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSave = async () => {
        if (isOverLimit) {
            showPremiumModal();
            return;
        }
        
        try {
            setSaving(true);
            
            // First apply common fields to orderEdits
            const updatedEdits = { ...orderEdits };
            
            Object.keys(updatedEdits).forEach(orderId => {
                Object.keys(commonFields).forEach(field => {
                    // Only apply if field toggle is ON and has a value
                    if (fieldToggles[field] && commonFields[field] !== '' && commonFields[field] !== null && commonFields[field] !== undefined) {
                        updatedEdits[orderId][field] = commonFields[field];
                    }
                });
            });

            const orderIds = Object.keys(updatedEdits);
            const shouldUseBatch = shouldUseBatchProcessing(orderIds.length, settings);

            console.log('🔍 Bulk Order Editor - Processing Decision:', {
                orderCount: orderIds.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);
                
                // Prepare batch data
                const batchData = Object.keys(updatedEdits).map(orderId => ({
                    id: orderId,
                    data: formatOrderDataForAPI(updatedEdits[orderId])
                }));

                await processBatch(
                    orderIds,
                    'bulkUpdate',
                    { updates: batchData },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'orders'
                );

                showSuccess(`Successfully updated ${orderIds.length} orders using batch processing`);
                navigate('/orders');
            } else {
                // Use regular processing for smaller datasets
                const updatePromises = Object.keys(updatedEdits).map(orderId => {
                    const formattedData = formatOrderDataForAPI(updatedEdits[orderId]);
                    return api.updateOrder(orderId, formattedData);
                });
                
                const results = await Promise.all(updatePromises);
                const successCount = results.filter(r => r.success).length;
                
                if (successCount === results.length) {
                    showSuccess(`Successfully updated ${successCount} orders`);
                    navigate('/orders');
                } else {
                    showError(`Updated ${successCount} of ${results.length} orders`);
                }
            }
            
        } catch (error) {
            showError(error.message || 'Failed to save orders');
        } finally {
            setSaving(false);
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pending': 'Pending Payment',
            'processing': 'Processing',
            'on-hold': 'On Hold',
            'completed': 'Completed',
            'cancelled': 'Cancelled',
            'refunded': 'Refunded',
            'failed': 'Failed',
        };
        return labels[status] || status;
    };

    if (loading && !usingBatchLoading) {
        return (
            <div className="page bulk-editor-page">
                <div className="page-container">
                    <LoadingState message="Loading orders..." />
                </div>
            </div>
        );
    }

    return (
        <div className="page bulk-editor-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Bulk Edit Orders</h1>
                        <p>Editing {orders.length} orders</p>
                    </div>
                    <div className="page-actions">
                        <button 
                            className="btn btn-secondary"
                            onClick={() => navigate('/orders')}
                        >
                            Cancel
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || isOverLimit}
                        >
                            {saving ? 'Saving...' : 'Save All Changes'}
                        </button>
                    </div>
                </div>

                {/* Bulk Edit Limit Notice */}
                {isOverLimit && (
                    <div className="bulk-limit-notice">
                        <div className="limit-notice-content">
                            <div className="notice-icon">⚠️</div>
                            <div className="notice-text">
                                <h3>Selection Limit Exceeded</h3>
                                <p>
                                    You have selected <strong>{selectedOrderIds.length} orders</strong>. 
                                    The free version allows bulk editing up to <strong>{BULK_EDIT_LIMIT} orders</strong> at once.
                                </p>
                                <p>
                                    Please reduce your selection to {BULK_EDIT_LIMIT} or fewer orders, or upgrade to Pro for unlimited bulk editing.
                                </p>
                            </div>
                            <div className="notice-actions">
                                <button 
                                    className="btn btn-primary"
                                    onClick={showPremiumModal}
                                >
                                    Upgrade to Pro
                                </button>
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => navigate('/orders')}
                                >
                                    Reduce Selection
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Mode Tabs */}
                <div className="edit-mode-tabs">
                    <button
                        className={`mode-tab ${editMode === 'common' ? 'active' : ''}`}
                        onClick={() => setEditMode('common')}
                    >
                        📝 Common Fields
                    </button>
                    <button
                        className={`mode-tab ${editMode === 'individual' ? 'active' : ''}`}
                        onClick={() => setEditMode('individual')}
                    >
                        📋 Individual Edit
                    </button>
                </div>

                {/* Common Fields Mode */}
                {editMode === 'common' && (
                    <div className="common-fields-section">
                        <div className="section-header">
                            <h3>Common Fields</h3>
                            <p>Toggle "Edit Together" ON to edit here, or OFF to edit individually in the table</p>
                        </div>

                        <div className="common-fields-grid">
                            {/* Order Status */}
                            <div className={`field-group ${!fieldToggles.status ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Order Status</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.status}
                                            onChange={() => toggleField('status')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.status}
                                    onChange={(e) => handleCommonFieldChange('status', e.target.value)}
                                    disabled={!fieldToggles.status}
                                >
                                    <option value="">Don't change</option>
                                    <option value="pending">Pending Payment</option>
                                    <option value="processing">Processing</option>
                                    <option value="on-hold">On Hold</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="refunded">Refunded</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>

                            {/* Payment Method */}
                            <div className={`field-group ${!fieldToggles.payment_method ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Payment Method</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.payment_method}
                                            onChange={() => toggleField('payment_method')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.payment_method}
                                    onChange={(e) => handleCommonFieldChange('payment_method', e.target.value)}
                                    disabled={!fieldToggles.payment_method}
                                >
                                    <option value="">Don't change</option>
                                    <option value="bacs">Direct Bank Transfer</option>
                                    <option value="cheque">Check Payments</option>
                                    <option value="cod">Cash on Delivery</option>
                                    <option value="paypal">PayPal</option>
                                    <option value="stripe">Credit Card (Stripe)</option>
                                </select>
                            </div>

                            {/* Payment Method Title */}
                            <div className={`field-group ${!fieldToggles.payment_method_title ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Payment Method Title</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.payment_method_title}
                                            onChange={() => toggleField('payment_method_title')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Edit individually"
                                    value={commonFields.payment_method_title}
                                    onChange={(e) => handleCommonFieldChange('payment_method_title', e.target.value)}
                                    disabled={!fieldToggles.payment_method_title}
                                />
                            </div>

                            {/* Transaction ID */}
                            <div className={`field-group ${!fieldToggles.transaction_id ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Transaction ID</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.transaction_id}
                                            onChange={() => toggleField('transaction_id')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Edit individually"
                                    value={commonFields.transaction_id}
                                    onChange={(e) => handleCommonFieldChange('transaction_id', e.target.value)}
                                    disabled={!fieldToggles.transaction_id}
                                />
                            </div>

                            {/* Customer Note */}
                            <div className={`field-group ${!fieldToggles.customer_note ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Customer Note</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.customer_note}
                                            onChange={() => toggleField('customer_note')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <textarea
                                    placeholder="Edit individually"
                                    value={commonFields.customer_note}
                                    onChange={(e) => handleCommonFieldChange('customer_note', e.target.value)}
                                    disabled={!fieldToggles.customer_note}
                                    rows="3"
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* Billing Section */}
                            <div className="field-group" style={{ gridColumn: '1 / -1', marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e5e7eb' }}>
                                <h4 style={{ marginBottom: '16px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>Billing Information</h4>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                                    {/* Billing First Name */}
                                    <div className={`field-group ${!fieldToggles.billing_first_name ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>First Name</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_first_name} onChange={() => toggleField('billing_first_name')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.billing_first_name} onChange={(e) => handleCommonFieldChange('billing_first_name', e.target.value)} disabled={!fieldToggles.billing_first_name} />
                                    </div>

                                    {/* Billing Last Name */}
                                    <div className={`field-group ${!fieldToggles.billing_last_name ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Last Name</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_last_name} onChange={() => toggleField('billing_last_name')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.billing_last_name} onChange={(e) => handleCommonFieldChange('billing_last_name', e.target.value)} disabled={!fieldToggles.billing_last_name} />
                                    </div>

                                    {/* Billing Email */}
                                    <div className={`field-group ${!fieldToggles.billing_email ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Email</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_email} onChange={() => toggleField('billing_email')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="email" placeholder="Edit individually" value={commonFields.billing_email} onChange={(e) => handleCommonFieldChange('billing_email', e.target.value)} disabled={!fieldToggles.billing_email} />
                                    </div>

                                    {/* Billing Phone */}
                                    <div className={`field-group ${!fieldToggles.billing_phone ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Phone</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_phone} onChange={() => toggleField('billing_phone')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="tel" placeholder="Edit individually" value={commonFields.billing_phone} onChange={(e) => handleCommonFieldChange('billing_phone', e.target.value)} disabled={!fieldToggles.billing_phone} />
                                    </div>
                                </div>

                                {/* Billing Address 1 */}
                                <div className={`field-group ${!fieldToggles.billing_address_1 ? 'disabled' : ''}`} style={{ marginTop: '16px' }}>
                                    <div className="field-header">
                                        <label>Address Line 1</label>
                                        <label className="toggle-switch">
                                            <input type="checkbox" checked={fieldToggles.billing_address_1} onChange={() => toggleField('billing_address_1')} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    <input type="text" placeholder="Edit individually" value={commonFields.billing_address_1} onChange={(e) => handleCommonFieldChange('billing_address_1', e.target.value)} disabled={!fieldToggles.billing_address_1} />
                                </div>

                                {/* Billing Address 2 */}
                                <div className={`field-group ${!fieldToggles.billing_address_2 ? 'disabled' : ''}`}>
                                    <div className="field-header">
                                        <label>Address Line 2</label>
                                        <label className="toggle-switch">
                                            <input type="checkbox" checked={fieldToggles.billing_address_2} onChange={() => toggleField('billing_address_2')} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    <input type="text" placeholder="Edit individually" value={commonFields.billing_address_2} onChange={(e) => handleCommonFieldChange('billing_address_2', e.target.value)} disabled={!fieldToggles.billing_address_2} />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginTop: '16px' }}>
                                    {/* Billing City */}
                                    <div className={`field-group ${!fieldToggles.billing_city ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>City</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_city} onChange={() => toggleField('billing_city')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.billing_city} onChange={(e) => handleCommonFieldChange('billing_city', e.target.value)} disabled={!fieldToggles.billing_city} />
                                    </div>

                                    {/* Billing State */}
                                    <div className={`field-group ${!fieldToggles.billing_state ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>State/Province</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_state} onChange={() => toggleField('billing_state')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.billing_state} onChange={(e) => handleCommonFieldChange('billing_state', e.target.value)} disabled={!fieldToggles.billing_state} />
                                    </div>

                                    {/* Billing Postcode */}
                                    <div className={`field-group ${!fieldToggles.billing_postcode ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Postcode/ZIP</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_postcode} onChange={() => toggleField('billing_postcode')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.billing_postcode} onChange={(e) => handleCommonFieldChange('billing_postcode', e.target.value)} disabled={!fieldToggles.billing_postcode} />
                                    </div>

                                    {/* Billing Country */}
                                    <div className={`field-group ${!fieldToggles.billing_country ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Country</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.billing_country} onChange={() => toggleField('billing_country')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.billing_country} onChange={(e) => handleCommonFieldChange('billing_country', e.target.value)} disabled={!fieldToggles.billing_country} />
                                    </div>
                                </div>
                            </div>

                            {/* Shipping Section */}
                            <div className="field-group" style={{ gridColumn: '1 / -1', marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e5e7eb' }}>
                                <h4 style={{ marginBottom: '16px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>Shipping Information</h4>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                                    {/* Shipping First Name */}
                                    <div className={`field-group ${!fieldToggles.shipping_first_name ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>First Name</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.shipping_first_name} onChange={() => toggleField('shipping_first_name')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.shipping_first_name} onChange={(e) => handleCommonFieldChange('shipping_first_name', e.target.value)} disabled={!fieldToggles.shipping_first_name} />
                                    </div>

                                    {/* Shipping Last Name */}
                                    <div className={`field-group ${!fieldToggles.shipping_last_name ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Last Name</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.shipping_last_name} onChange={() => toggleField('shipping_last_name')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.shipping_last_name} onChange={(e) => handleCommonFieldChange('shipping_last_name', e.target.value)} disabled={!fieldToggles.shipping_last_name} />
                                    </div>
                                </div>

                                {/* Shipping Address 1 */}
                                <div className={`field-group ${!fieldToggles.shipping_address_1 ? 'disabled' : ''}`} style={{ marginTop: '16px' }}>
                                    <div className="field-header">
                                        <label>Address Line 1</label>
                                        <label className="toggle-switch">
                                            <input type="checkbox" checked={fieldToggles.shipping_address_1} onChange={() => toggleField('shipping_address_1')} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    <input type="text" placeholder="Edit individually" value={commonFields.shipping_address_1} onChange={(e) => handleCommonFieldChange('shipping_address_1', e.target.value)} disabled={!fieldToggles.shipping_address_1} />
                                </div>

                                {/* Shipping Address 2 */}
                                <div className={`field-group ${!fieldToggles.shipping_address_2 ? 'disabled' : ''}`}>
                                    <div className="field-header">
                                        <label>Address Line 2</label>
                                        <label className="toggle-switch">
                                            <input type="checkbox" checked={fieldToggles.shipping_address_2} onChange={() => toggleField('shipping_address_2')} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    <input type="text" placeholder="Edit individually" value={commonFields.shipping_address_2} onChange={(e) => handleCommonFieldChange('shipping_address_2', e.target.value)} disabled={!fieldToggles.shipping_address_2} />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginTop: '16px' }}>
                                    {/* Shipping City */}
                                    <div className={`field-group ${!fieldToggles.shipping_city ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>City</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.shipping_city} onChange={() => toggleField('shipping_city')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.shipping_city} onChange={(e) => handleCommonFieldChange('shipping_city', e.target.value)} disabled={!fieldToggles.shipping_city} />
                                    </div>

                                    {/* Shipping State */}
                                    <div className={`field-group ${!fieldToggles.shipping_state ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>State/Province</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.shipping_state} onChange={() => toggleField('shipping_state')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.shipping_state} onChange={(e) => handleCommonFieldChange('shipping_state', e.target.value)} disabled={!fieldToggles.shipping_state} />
                                    </div>

                                    {/* Shipping Postcode */}
                                    <div className={`field-group ${!fieldToggles.shipping_postcode ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Postcode/ZIP</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.shipping_postcode} onChange={() => toggleField('shipping_postcode')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.shipping_postcode} onChange={(e) => handleCommonFieldChange('shipping_postcode', e.target.value)} disabled={!fieldToggles.shipping_postcode} />
                                    </div>

                                    {/* Shipping Country */}
                                    <div className={`field-group ${!fieldToggles.shipping_country ? 'disabled' : ''}`}>
                                        <div className="field-header">
                                            <label>Country</label>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={fieldToggles.shipping_country} onChange={() => toggleField('shipping_country')} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <input type="text" placeholder="Edit individually" value={commonFields.shipping_country} onChange={(e) => handleCommonFieldChange('shipping_country', e.target.value)} disabled={!fieldToggles.shipping_country} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="apply-section">
                            <button 
                                className="btn btn-primary btn-lg"
                                onClick={() => applyCommonFields()}
                                disabled={saving || isOverLimit}
                            >
                                {saving ? 'Saving...' : isOverLimit ? 'Selection Limit Exceeded' : `Apply Common Fields to ${selectedOrderIds.length} Selected Orders`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Individual Edit Mode */}
                {editMode === 'individual' && (
                    <div className="individual-edit-section">
                        <div className="section-header">
                            <h3>Edit Individual Orders</h3>
                            <p>Fields with "Edit Together" OFF appear here for individual editing</p>
                        </div>

                        <div 
                            className="products-table-wrapper" 
                            ref={tableScrollRef}
                        >
                            <table className="bulk-edit-table">
                                <thead>
                                    <tr>
                                        <th style={{width: '150px'}}>Order #</th>
                                        <th style={{width: '150px'}}>Customer</th>
                                        <th style={{width: '100px'}}>Total</th>
                                        {!fieldToggles.status && <th style={{width: '140px'}}>Status</th>}
                                        {!fieldToggles.payment_method && <th style={{width: '150px'}}>Payment Method</th>}
                                        {!fieldToggles.payment_method_title && <th style={{width: '180px'}}>Payment Title</th>}
                                        {!fieldToggles.transaction_id && <th style={{width: '150px'}}>Transaction ID</th>}
                                        {!fieldToggles.customer_note && <th style={{width: '200px'}}>Customer Note</th>}
                                        {!fieldToggles.billing_first_name && <th style={{width: '150px'}}>Bill First Name</th>}
                                        {!fieldToggles.billing_last_name && <th style={{width: '150px'}}>Bill Last Name</th>}
                                        {!fieldToggles.billing_email && <th style={{width: '180px'}}>Bill Email</th>}
                                        {!fieldToggles.billing_phone && <th style={{width: '150px'}}>Bill Phone</th>}
                                        {!fieldToggles.billing_address_1 && <th style={{width: '200px'}}>Bill Address 1</th>}
                                        {!fieldToggles.billing_address_2 && <th style={{width: '200px'}}>Bill Address 2</th>}
                                        {!fieldToggles.billing_city && <th style={{width: '150px'}}>Bill City</th>}
                                        {!fieldToggles.billing_state && <th style={{width: '150px'}}>Bill State</th>}
                                        {!fieldToggles.billing_postcode && <th style={{width: '120px'}}>Bill Postcode</th>}
                                        {!fieldToggles.billing_country && <th style={{width: '120px'}}>Bill Country</th>}
                                        {!fieldToggles.shipping_first_name && <th style={{width: '150px'}}>Ship First Name</th>}
                                        {!fieldToggles.shipping_last_name && <th style={{width: '150px'}}>Ship Last Name</th>}
                                        {!fieldToggles.shipping_address_1 && <th style={{width: '200px'}}>Ship Address 1</th>}
                                        {!fieldToggles.shipping_address_2 && <th style={{width: '200px'}}>Ship Address 2</th>}
                                        {!fieldToggles.shipping_city && <th style={{width: '150px'}}>Ship City</th>}
                                        {!fieldToggles.shipping_state && <th style={{width: '150px'}}>Ship State</th>}
                                        {!fieldToggles.shipping_postcode && <th style={{width: '120px'}}>Ship Postcode</th>}
                                        {!fieldToggles.shipping_country && <th style={{width: '120px'}}>Ship Country</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map(order => (
                                        <tr key={order.id}>
                                            <td>
                                                <div className="order-cell">
                                                    <strong>#{order.number || order.id}</strong>
                                                    <div style={{fontSize: '12px', color: '#6b7280'}}>
                                                        {new Date(order.date_created).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{fontSize: '13px'}}>
                                                    <div style={{fontWeight: '500'}}>{order.billing?.first_name} {order.billing?.last_name}</div>
                                                    <div style={{fontSize: '12px', color: '#6b7280'}}>{order.billing?.email}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <strong>${parseFloat(order.total).toFixed(2)}</strong>
                                            </td>
                                            {!fieldToggles.status && (
                                                <td>
                                                    <select
                                                        value={orderEdits[order.id]?.status || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'status', e.target.value)}
                                                        className="table-select"
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="processing">Processing</option>
                                                        <option value="on-hold">On Hold</option>
                                                        <option value="completed">Completed</option>
                                                        <option value="cancelled">Cancelled</option>
                                                        <option value="refunded">Refunded</option>
                                                        <option value="failed">Failed</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.payment_method && (
                                                <td>
                                                    <select
                                                        value={orderEdits[order.id]?.payment_method || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'payment_method', e.target.value)}
                                                        className="table-select"
                                                    >
                                                        <option value="bacs">Bank Transfer</option>
                                                        <option value="cheque">Check</option>
                                                        <option value="cod">Cash on Delivery</option>
                                                        <option value="paypal">PayPal</option>
                                                        <option value="stripe">Credit Card</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.payment_method_title && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.payment_method_title || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'payment_method_title', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.transaction_id && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.transaction_id || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'transaction_id', e.target.value)}
                                                        className="table-input"
                                                        placeholder="Transaction ID"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.customer_note && (
                                                <td>
                                                    <textarea
                                                        value={orderEdits[order.id]?.customer_note || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'customer_note', e.target.value)}
                                                        className="table-input"
                                                        rows="2"
                                                        style={{resize: 'vertical', minHeight: '40px'}}
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_first_name && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_first_name || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_first_name', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_last_name && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_last_name || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_last_name', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_email && (
                                                <td>
                                                    <input
                                                        type="email"
                                                        value={orderEdits[order.id]?.billing_email || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_email', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_phone && (
                                                <td>
                                                    <input
                                                        type="tel"
                                                        value={orderEdits[order.id]?.billing_phone || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_phone', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_address_1 && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_address_1 || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_address_1', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_address_2 && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_address_2 || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_address_2', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_city && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_city || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_city', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_state && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_state || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_state', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_postcode && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_postcode || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_postcode', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.billing_country && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.billing_country || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'billing_country', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_first_name && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_first_name || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_first_name', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_last_name && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_last_name || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_last_name', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_address_1 && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_address_1 || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_address_1', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_address_2 && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_address_2 || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_address_2', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_city && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_city || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_city', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_state && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_state || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_state', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_postcode && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_postcode || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_postcode', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_country && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={orderEdits[order.id]?.shipping_country || ''}
                                                        onChange={(e) => handleOrderFieldChange(order.id, 'shipping_country', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        <div className="apply-section">
                            <button 
                                className="btn btn-primary btn-lg"
                                onClick={() => applyIndividualChanges()}
                                disabled={saving || isOverLimit}
                            >
                                {saving ? 'Saving...' : isOverLimit ? 'Selection Limit Exceeded' : `Apply Individual Changes to ${selectedOrderIds.length} Orders`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Summary */}
                <div className="bulk-edit-summary">
                    <div className="summary-card">
                        <h4>Summary</h4>
                        <div className="summary-stats">
                            <div className="stat">
                                <span className="stat-label">Orders Selected:</span>
                                <span className="stat-value">{orders.length}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Mode:</span>
                                <span className="stat-value">{editMode === 'common' ? 'Common Fields' : 'Individual Edit'}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Total Value:</span>
                                <span className="stat-value">
                                    ${orders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Batch Loading Modal */}
            <BatchProgressModal
                isOpen={showLoadingModal}
                onClose={() => {
                    setShowLoadingModal(false);
                    resetLoadingProgress();
                }}
                progress={loadingProgress}
                currentBatch={loadingProgress.currentBatch}
                totalBatches={loadingProgress.totalBatches}
                processedItems={loadingProgress.loadedItems}
                totalItems={loadingProgress.totalItems}
                operationType="orders"
                isCompleted={loadingProgress.isCompleted}
                error={loadingProgress.error}
                isLoading={true}
            />

            {/* Batch Progress Modal */}
            <BatchProgressModal
                isOpen={showBatchModal}
                onClose={() => {
                    setShowBatchModal(false);
                    resetProgress();
                }}
                progress={batchProgress}
                currentBatch={batchProgress.currentBatch}
                totalBatches={batchProgress.totalBatches}
                processedItems={batchProgress.processedItems}
                totalItems={batchProgress.totalItems}
                operationType="orders"
                isCompleted={batchProgress.isCompleted}
                error={batchProgress.error}
            />
        </div>
    );
};

export default BulkOrderEditor;
